// Follows "HR Design rules.md": GEN, LOAD, TBL
import * as React from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from './skeleton';

/*
 * DescriptionList — key–value facts about one record (TBL-01: tables are for many records, description
 * lists for one). Renders a real <dl>; each DescriptionItem is a <div> wrapping <dt> + <dd>.
 *
 * layout="inline"  term left, value right, hairline between rows (profile cards)
 * layout="stacked" term above value, in a responsive grid (detail pages)
 */

function DescriptionList({
  className,
  layout = 'inline',
  ...props
}: React.ComponentProps<'dl'> & { layout?: 'inline' | 'stacked' }) {
  return (
    <dl
      data-slot="description-list"
      data-layout={layout}
      className={cn(
        'group/description-list text-sm',
        layout === 'inline' ? 'grid' : 'grid gap-x-6 gap-y-4 sm:grid-cols-2',
        className
      )}
      {...props}
    />
  );
}

function DescriptionItem({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="description-item"
      className={cn(
        'grid gap-1',
        'group-data-[layout=inline]/description-list:flex group-data-[layout=inline]/description-list:items-baseline group-data-[layout=inline]/description-list:justify-between group-data-[layout=inline]/description-list:gap-4 group-data-[layout=inline]/description-list:border-b group-data-[layout=inline]/description-list:py-2 group-data-[layout=inline]/description-list:last:border-b-0',
        className
      )}
      {...props}
    />
  );
}

/** The label. A leading icon is sized automatically and inherits the muted colour. */
function DescriptionTerm({ className, ...props }: React.ComponentProps<'dt'>) {
  return (
    <dt
      data-slot="description-term"
      className={cn(
        'flex shrink-0 items-center gap-2 text-muted-foreground [&>svg]:pointer-events-none [&>svg]:size-4 [&>svg]:shrink-0',
        className
      )}
      {...props}
    />
  );
}

/** The value. Numbers inside should use tabular figures (GEN-08). */
function DescriptionDetails({ className, ...props }: React.ComponentProps<'dd'>) {
  return (
    <dd
      data-slot="description-details"
      className={cn(
        'min-w-0 break-words font-medium text-foreground [overflow-wrap:anywhere]',
        'group-data-[layout=inline]/description-list:text-right',
        className
      )}
      {...props}
    />
  );
}

/** LOAD-03 — mirrors a DescriptionList row for row: same layout, same row height, varied widths. */
const TERM_WIDTHS = ['w-20', 'w-24', 'w-16', 'w-28', 'w-20', 'w-24'];
const VALUE_WIDTHS = ['w-24', 'w-32', 'w-20', 'w-28', 'w-36', 'w-16'];

function DescriptionListSkeleton({
  rows = 5,
  layout = 'inline',
  className,
}: {
  rows?: number;
  layout?: 'inline' | 'stacked';
  className?: string;
}) {
  return (
    <div
      data-slot="description-list-skeleton"
      data-layout={layout}
      aria-hidden="true"
      className={cn(
        'group/description-list text-sm',
        layout === 'inline' ? 'grid' : 'grid gap-x-6 gap-y-4 sm:grid-cols-2',
        className
      )}
    >
      {Array.from({ length: rows }, (_, i) => (
        <DescriptionItem key={i}>
          <Skeleton className={cn('h-3.5', TERM_WIDTHS[i % TERM_WIDTHS.length])} />
          <Skeleton className={cn('my-[3px] h-3.5', VALUE_WIDTHS[i % VALUE_WIDTHS.length])} />
        </DescriptionItem>
      ))}
    </div>
  );
}

export {
  DescriptionList,
  DescriptionItem,
  DescriptionTerm,
  DescriptionDetails,
  DescriptionListSkeleton,
};
